import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Upload, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/utils/api";

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [company, setCompany] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    business_type: "",
    registration_number: "",
    tax_id: "",
    website: ""
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const response = await api.get('/company/settings');
      setCompany(response.data);
      setFormData({
        name: response.data.name || "",
        address: response.data.address || "",
        phone: response.data.phone || "",
        email: response.data.email || "",
        business_type: response.data.business_type || "",
        registration_number: response.data.registration_number || "",
        tax_id: response.data.tax_id || "",
        website: response.data.website || ""
      });
      
      if (response.data.logo_url) {
        setLogoPreview(`${process.env.REACT_APP_BACKEND_URL}${response.data.logo_url}`);
      }
    } catch (error) {
      toast.error("Failed to load company settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.patch('/company/settings', formData);
      toast.success("Company settings updated successfully");
      
      // Update user's company info in localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.company) {
        user.company = { ...user.company, ...formData };
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      fetchCompanySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await api.post('/company/logo', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success("Logo uploaded successfully");
      setLogoPreview(URL.createObjectURL(file));
      
      // Update user's company info in localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.company) {
        user.company.logo_url = response.data.logo_url;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      fetchCompanySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div data-testid="company-settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Company Settings
        </h1>
        <p className="text-zinc-500">Manage your company information and branding</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo Upload Section */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Building2 size={20} />
              Company Logo
            </h2>
            
            <div className="space-y-4">
              {/* Logo Preview */}
              <div className="flex justify-center">
                {logoPreview ? (
                  <img 
                    src={logoPreview} 
                    alt="Company Logo" 
                    className="h-32 w-32 object-contain border-2 border-zinc-200 rounded-lg p-2"
                  />
                ) : (
                  <div className="h-32 w-32 bg-zinc-100 border-2 border-dashed border-zinc-300 rounded-lg flex items-center justify-center">
                    <Building2 size={40} className="text-zinc-400" />
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div>
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-4 py-2 bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold rounded-md transition-all active:scale-95">
                    <Upload size={16} />
                    {uploading ? "Uploading..." : "Upload Logo"}
                  </div>
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </Label>
                <p className="text-xs text-zinc-500 mt-2 text-center">
                  PNG or JPG. Max size 5MB.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Company Information Form */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-bold mb-4">Company Information</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="business_type">Business Type</Label>
                  <Input
                    id="business_type"
                    placeholder="e.g., Gym, Yoga Studio, Fitness Center"
                    value={formData.business_type}
                    onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  rows={2}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="registration_number">Registration Number</Label>
                  <Input
                    id="registration_number"
                    placeholder="Business registration number"
                    value={formData.registration_number}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="tax_id">Tax ID</Label>
                  <Input
                    id="tax_id"
                    placeholder="Tax identification number"
                    value={formData.tax_id}
                    onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://www.example.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
                disabled={saving}
              >
                <Save size={16} className="mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
